import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type ChartConfig } from '@/components/ui/chart';

import { AbsoluteChangeIndicator } from './AbsoluteChangeIndicator';
import { AI_AGENTS_METRIC_CHART_CONFIGS, type AiAgentMetricKey } from './aiAgentsInsightsConfig';
import { AiAgentsInsightsExport } from './AiAgentsInsightsExport';
import { AiAgentsTable } from './AiAgentsTable';
import { useAiAgentsInsights } from './hooks/useAiAgentsInsights';
import { useSelectedAiAgentMetric } from './hooks/useSelectedAiAgentMetric';
import { getComparisonPeriodKey } from './PercentChangeIndicator';
import { useComparisonMode } from '../../hooks/useComparisonMode';
import { selectAnalyticsDates } from '../../redux/selectors';
import {
  formatCountDelta,
  formatCsatDelta,
  formatPercentDelta,
  formatSecondsDelta,
} from '../../utils/deltaFormatters';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { KpiTile } from '../KpiTile/KpiTile';
import { TimeseriesChart } from '../TimeseriesChart/TimeseriesChart';

const CHART_COLOR = 'var(--chart-1)';

const EMPTY_VALUE_PLACEHOLDER = '-';

const formatCsat = (value: number): string => `${value.toFixed(1)}/5`;
const formatPercent = (value: number): string => `${Math.round(value)}%`;
const formatSeconds = (value: number): string => `${value.toFixed(1)}s`;
const formatCount = (value: number): string => value.toLocaleString();

const DELTA_FORMATTERS: Record<AiAgentMetricKey, (delta: number) => string> = {
  csat: formatCsatDelta,
  containment: formatPercentDelta,
  responseTime: formatSecondsDelta,
  totalConversations: formatCountDelta,
};

/** Decimal places kept when deciding whether a delta rounds to neutral. */
const DELTA_PRECISIONS: Record<AiAgentMetricKey, number> = {
  csat: 1,
  containment: 0,
  responseTime: 1,
  totalConversations: 0,
};

// ---------------------------------------------------------------------------
// Tile definitions
// ---------------------------------------------------------------------------

/** Metrics where a lower value is better (e.g. faster response time). */
const INVERT_COLOR_METRICS: ReadonlySet<AiAgentMetricKey> = new Set(['responseTime']);

interface KpiTileDefinition {
  key: AiAgentMetricKey;
  labelKey: string;
  tooltipKey: string;
  formattedValue: string;
  current: number;
  previous: number;
  isMetricEmpty: boolean;
}

const buildKpiTiles = (
  kpiValues: Record<AiAgentMetricKey, { current: number; previous: number }>,
  isEmpty: boolean
): KpiTileDefinition[] => {
  // CSAT and response time can be absent even when other metrics have data
  // (no reviews of either kind, or no AI turns in the period). Treat a
  // non-positive value as "no samples" and render the placeholder instead of
  // a misleading "0.0/5" or "0s" — a true 0s LLM response is impossible.
  const isCsatEmpty = isEmpty || kpiValues.csat.current <= 0;
  const isResponseTimeEmpty = isEmpty || kpiValues.responseTime.current <= 0;

  return [
    {
      key: 'totalConversations',
      labelKey: 'analytics.total_conversations',
      tooltipKey: 'analytics.ai_agents_overview_tooltips.total_conversations',
      formattedValue: isEmpty
        ? EMPTY_VALUE_PLACEHOLDER
        : formatCount(kpiValues.totalConversations.current),
      current: kpiValues.totalConversations.current,
      previous: kpiValues.totalConversations.previous,
      isMetricEmpty: isEmpty,
    },
    {
      key: 'csat',
      labelKey: 'analytics.overview_new.avg_ai_csat',
      tooltipKey: 'analytics.overview_new.avg_ai_csat_tooltip',
      formattedValue: isCsatEmpty ? EMPTY_VALUE_PLACEHOLDER : formatCsat(kpiValues.csat.current),
      current: kpiValues.csat.current,
      previous: kpiValues.csat.previous,
      isMetricEmpty: isCsatEmpty,
    },
    {
      key: 'containment',
      labelKey: 'analytics.containment',
      tooltipKey: 'analytics.overview_new.containment_tooltip',
      formattedValue: isEmpty
        ? EMPTY_VALUE_PLACEHOLDER
        : formatPercent(kpiValues.containment.current),
      current: kpiValues.containment.current,
      previous: kpiValues.containment.previous,
      isMetricEmpty: isEmpty,
    },
    {
      key: 'responseTime',
      labelKey: 'analytics.response_time',
      tooltipKey: 'analytics.ai_agents_overview_tooltips.response_time',
      formattedValue: isResponseTimeEmpty
        ? EMPTY_VALUE_PLACEHOLDER
        : formatSeconds(kpiValues.responseTime.current),
      current: kpiValues.responseTime.current,
      previous: kpiValues.responseTime.previous,
      isMetricEmpty: isResponseTimeEmpty,
    },
  ];
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * AI Agents Insights widget.
 *
 * Four KPI tiles (total conversations, CSAT, containment, response time) backed
 * by `ai_agents_overview`, with a time-series chart (per-bucket data from
 * `ai_agents_timeseries`) that follows the selected tile.
 */
export const AiAgentsInsights = () => {
  const { t } = useTranslation();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { data: insights, isLoading, error, isEmpty } = useAiAgentsInsights();
  const { selectedMetric, setSelectedMetric } = useSelectedAiAgentMetric();

  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));
  const isSingleDay = isSingleDayRange(startDate, endDate);
  const dateRange = formatDateRangeLabel(startDate, endDate);
  const currentConfig = AI_AGENTS_METRIC_CHART_CONFIGS[selectedMetric];

  // Drive the chart x-axis from the selected range (and the previous-period
  // range when comparing) so every day renders even without returned data.
  const period = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const comparisonPeriod = useMemo(() => {
    const { startDate: start, endDate: end } = getComparisonDates(startDate, endDate);
    return { start, end };
  }, [startDate, endDate]);

  const chartConfig = useMemo<ChartConfig>(
    () => ({ value: { label: t(currentConfig.label), color: CHART_COLOR } }),
    [currentConfig, t]
  );

  const kpiTiles = useMemo(
    () => buildKpiTiles(insights.kpiValues, isEmpty),
    [insights.kpiValues, isEmpty]
  );

  const timeseries = useMemo(
    () => ({
      isLoading,
      error: error ?? undefined,
      data: insights.timeseries[selectedMetric],
      onRefetch: () => undefined,
    }),
    [error, insights.timeseries, isLoading, selectedMetric]
  );

  const comparisonTimeseries = useMemo(
    () => ({
      isLoading,
      error: error ?? undefined,
      data: insights.comparisonTimeseries[selectedMetric],
    }),
    [error, insights.comparisonTimeseries, isLoading, selectedMetric]
  );

  const csatBreakdown = selectedMetric === 'csat' ? insights.csatBreakdown : null;
  const comparisonCsatBreakdown =
    selectedMetric === 'csat' && isComparing ? insights.comparisonCsatBreakdown : null;

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.ai_agents_overview')}</CardTitle>
            <CardDescription>{dateRange}</CardDescription>
          </div>
          {!isEmpty && <AiAgentsInsightsExport />}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {kpiTiles.map((tile) => (
            <KpiTile
              key={tile.key}
              label={t(tile.labelKey)}
              tooltip={t(tile.tooltipKey)}
              value={tile.formattedValue}
              isLoading={isLoading}
              isSelected={selectedMetric === tile.key}
              onClick={() => setSelectedMetric(tile.key)}
              comparison={
                isComparing && !tile.isMetricEmpty ? (
                  <AbsoluteChangeIndicator
                    current={tile.current}
                    previous={tile.previous}
                    formatDelta={DELTA_FORMATTERS[tile.key]}
                    precision={DELTA_PRECISIONS[tile.key]}
                    comparisonLabel={comparisonLabel}
                    invertColors={INVERT_COLOR_METRICS.has(tile.key)}
                    className="mt-1"
                  />
                ) : undefined
              }
            />
          ))}
        </div>

        {/* Single-day rule: charts only make sense for multi-day ranges. */}
        {!isSingleDay && timeseries.data.length > 0 && (
          <TimeseriesChart
            timeseries={timeseries}
            chartConfig={chartConfig}
            currentConfig={currentConfig}
            t={t}
            period={period}
            comparisonPeriod={comparisonPeriod}
            isComparing={isComparing}
            comparisonTimeseries={comparisonTimeseries}
            csatBreakdown={csatBreakdown}
            comparisonCsatBreakdown={comparisonCsatBreakdown}
          />
        )}

        <AiAgentsTable />
      </CardContent>
    </Card>
  );
};
